import isNull from 'lodash-es/isNull'

import { EMPTY_LIST } from '../../constants/common'
import {
  isSubAll,
  isSubArchive,
  isSubRead,
  isSubUnread,
} from '../../utils/route'
import {
  getEntityActionTypeSelectOptions,
  isEditEntityActionType,
} from '../Entity'

import {
  REQUEST_ACTION_ARCHIVE_ITEM,
  REQUEST_ACTION_ARCHIVE_MASS,
  REQUEST_ACTION_ASSIGN_ITEM,
  REQUEST_ACTION_ASSIGN_MASS,
  REQUEST_ACTION_CONNECT_DOC_FLOW_ITEM,
  REQUEST_ACTION_CONNECT_DOC_FLOW_MASS,
  REQUEST_ACTION_CONNECT_SERVICE_REQUEST_ITEM,
  REQUEST_ACTION_CONNECT_SERVICE_REQUEST_MASS,
  REQUEST_ACTION_CREATE_DOC_FLOW_ITEM,
  REQUEST_ACTION_CREATE_DOC_FLOW_MASS,
  REQUEST_ACTION_DUE_DATE_ITEM,
  REQUEST_ACTION_DUE_DATE_MASS,
  REQUEST_ACTION_EDIT_CATEGORY_ITEM,
  REQUEST_ACTION_EDIT_CATEGORY_MASS,
  REQUEST_ACTION_EDIT_LABEL_ITEM,
  REQUEST_ACTION_EDIT_LABEL_MASS,
  REQUEST_ACTION_EDIT_PROVIDER_ITEM,
  REQUEST_ACTION_EDIT_PROVIDER_MASS,
  REQUEST_ACTION_GROUP_SERVICE_REQUESTS_MASS,
  REQUEST_ACTION_MARK_AS_PRIVATE,
  REQUEST_ACTION_MARK_AS_PRIVATE_MASS,
  REQUEST_ACTION_MARK_AS_PUBLIC,
  REQUEST_ACTION_MARK_AS_PUBLIC_MASS,
  REQUEST_ACTION_MARK_AS_READ,
  REQUEST_ACTION_MARK_AS_READ_MASS,
  REQUEST_ACTION_MARK_AS_UNREAD,
  REQUEST_ACTION_MARK_AS_UNREAD_MASS,
  REQUEST_ACTION_RESTORE_ITEM,
  REQUEST_ACTION_RESTORE_MASS,
  REQUEST_ACTION_UPDATE_STATUS_ITEM,
  REQUEST_ACTION_UPDATE_STATUS_MASS,
  REQUEST_ITEM_ACTION_TYPES,
  RequestTableExportOptionValue,
} from './Request.constants.actions'
import { isRequestArchived } from './Request.utils'
import {
  getIsRequestArchiveBlockedByStatus,
  getRequestArchiveBlockedByStatusTooltipProps,
} from './Request.utils.archive'

function getRequestMassActionsArchiveState({ selectAllPages, selected, sub }) {
  const archivedCount = selected.filter(isRequestArchived).length
  const hasArchivedAndActive =
    archivedCount > 0 && archivedCount < selected.length

  const isMixed = isSubAll(sub) && (selectAllPages || hasArchivedAndActive)

  return {
    isArchive: isSubAll(sub)
      ? !isMixed && archivedCount > 0
      : isSubArchive(sub),
    isMixed,
  }
}

export function isRequestSelectionWithArchived({
  selectAllPages,
  selected,
  sub,
}) {
  const { isArchive, isMixed } = getRequestMassActionsArchiveState({
    selectAllPages,
    selected,
    sub,
  })

  return isArchive || isMixed
}

export function getRequestMassActionUnreadFilter(sub) {
  return isSubUnread(sub) ? true : isSubRead(sub) ? false : undefined
}

export function getItemActions({
  canConnectDocFlow,
  canCreateDocFlow,
  item,
  t,
  ukConfig,
}) {
  const { permissions, unread, visible_to_dwellers: isVisibleToDwellers } = item

  const isArchiveBlockedByStatus = getIsRequestArchiveBlockedByStatus({
    statusId: item.status,
    ukConfig,
  })

  const items = [
    {
      label: 'Request.EditAssigneeActionOptionKey',
      permission: permissions.can_edit_assignee,
      value: REQUEST_ACTION_ASSIGN_ITEM,
    },
    {
      label: 'Request.EditProviderActionOptionKey',
      permission: permissions.can_edit_contractors,
      value: REQUEST_ACTION_EDIT_PROVIDER_ITEM,
    },
    {
      label: 'Request.ChangeStatusActionOptionKey',
      permission: permissions.can_edit_status,
      value: REQUEST_ACTION_UPDATE_STATUS_ITEM,
    },
    {
      label: 'Request.ChangeDeadlineActionOptionKey',
      permission: permissions.can_edit_due_date,
      value: REQUEST_ACTION_DUE_DATE_ITEM,
    },
    {
      label: 'Request.EditLabelActionOptionKey',
      permission: permissions.can_edit_labels,
      value: REQUEST_ACTION_EDIT_LABEL_ITEM,
    },
    {
      label: 'Request.EditCategoryActionOptionKey',
      permission: permissions.can_edit_category,
      value: REQUEST_ACTION_EDIT_CATEGORY_ITEM,
    },
    {
      label: 'Request.ConnectServiceRequestsActionOptionKey',
      permission: permissions.can_add_connected_requests,
      value: REQUEST_ACTION_CONNECT_SERVICE_REQUEST_ITEM,
    },
    {
      label: 'Request.ConnectDocFlowActionOptionKey',
      permission: canConnectDocFlow,
      value: REQUEST_ACTION_CONNECT_DOC_FLOW_ITEM,
    },
    {
      label: 'Request.CreateDocFlowActionOptionKey',
      permission: canConnectDocFlow && canCreateDocFlow,
      value: REQUEST_ACTION_CREATE_DOC_FLOW_ITEM,
    },
    {
      label: 'Request.MarkAsUnreadActionOptionKey',
      permission: permissions.can_edit_unread_status && !unread,
      value: REQUEST_ACTION_MARK_AS_UNREAD,
    },
    {
      label: 'Request.MarkAsReadActionOptionKey',
      permission: permissions.can_edit_unread_status && unread,
      value: REQUEST_ACTION_MARK_AS_READ,
    },
    {
      label: 'Request.MarkAsPublicActionOptionKey',
      permission:
        permissions.can_edit_visibility_to_dwellers && !isVisibleToDwellers,
      value: REQUEST_ACTION_MARK_AS_PUBLIC,
    },
    {
      label: 'Request.MarkAsPrivateActionOptionKey',
      permission:
        permissions.can_edit_visibility_to_dwellers && isVisibleToDwellers,
      value: REQUEST_ACTION_MARK_AS_PRIVATE,
    },
    null,
    {
      ...getRequestArchiveBlockedByStatusTooltipProps({
        isBlocked: isArchiveBlockedByStatus,
        statusId: item.status,
        t,
      }),
      disabled: isArchiveBlockedByStatus,
      label: 'Request.ArchiveActionOptionKey',
      permission: permissions.can_archive,
      value: REQUEST_ACTION_ARCHIVE_ITEM,
    },
    {
      label: 'Request.RestoreActionOptionKey',
      permission: permissions.can_restore,
      value: REQUEST_ACTION_RESTORE_ITEM,
    },
  ]

  let list = items.filter(i => (i && !!i.permission) || !i)

  if (isNull(list[0]) || isNull(list[list.length - 1])) {
    list = list.filter(i => !isNull(i))
  }

  return list
}

export function getMassActions({
  canCreateDocFlow,
  permissions,
  selectAllPages,
  selected,
  sub,
  t,
}) {
  const { isArchive, isMixed } = getRequestMassActionsArchiveState({
    selectAllPages,
    selected,
    sub,
  })

  const isEditable = !isArchive && !isMixed

  const items = [
    {
      label: t('Request.EditAssigneeActionOptionKey'),
      permission: permissions.can_edit_assignee && isEditable,
      value: REQUEST_ACTION_ASSIGN_MASS,
    },
    {
      label: t('Request.EditProviderActionOptionKey'),
      permission: permissions.can_edit_contractors && isEditable,
      value: REQUEST_ACTION_EDIT_PROVIDER_MASS,
    },
    {
      label: t('Request.ChangeStatusActionOptionKey'),
      permission: permissions.can_edit_status && isEditable,
      value: REQUEST_ACTION_UPDATE_STATUS_MASS,
    },
    {
      label: t('Request.ChangeDeadlineActionOptionKey'),
      permission: permissions.can_edit_due_date && isEditable,
      value: REQUEST_ACTION_DUE_DATE_MASS,
    },
    {
      label: t('Request.EditLabelActionOptionKey'),
      permission: permissions.can_edit_labels && isEditable,
      value: REQUEST_ACTION_EDIT_LABEL_MASS,
    },
    {
      label: t('Request.EditCategoryActionOptionKey'),
      permission: permissions.can_edit_category && isEditable,
      value: REQUEST_ACTION_EDIT_CATEGORY_MASS,
    },
    {
      label: t('Request.ConnectServiceRequestsActionOptionKey'),
      permission: permissions.can_add_connected_requests && isEditable,
      value: REQUEST_ACTION_CONNECT_SERVICE_REQUEST_MASS,
    },
    {
      disabled: selectAllPages,
      label: t('Request.ConnectDocFlowActionOptionKey'),
      permission: permissions.can_add_connected_requests,
      value: REQUEST_ACTION_CONNECT_DOC_FLOW_MASS,
    },
    {
      label: t('Request.CreateDocFlowActionOptionKey'),
      permission: permissions.can_add_connected_requests && canCreateDocFlow,
      value: REQUEST_ACTION_CREATE_DOC_FLOW_MASS,
    },
    {
      disabled: selected.length === 1,
      label: t('Request.AttachToEachOtherMassActionOptionKey'),
      permission: permissions.can_group_requests,
      value: REQUEST_ACTION_GROUP_SERVICE_REQUESTS_MASS,
    },
    {
      label: t('Request.MarkAsUnreadActionOptionKey'),
      permission:
        permissions.can_edit_unread_status && isEditable && !isSubUnread(sub),
      value: REQUEST_ACTION_MARK_AS_UNREAD_MASS,
    },
    {
      label: t('Request.MarkAsReadActionOptionKey'),
      permission:
        permissions.can_edit_unread_status && isEditable && !isSubRead(sub),
      value: REQUEST_ACTION_MARK_AS_READ_MASS,
    },
    {
      label: t('Request.MarkAsPublicActionOptionKey'),
      permission: permissions.can_edit_visibility_to_dwellers && isEditable,
      value: REQUEST_ACTION_MARK_AS_PUBLIC_MASS,
    },
    {
      label: t('Request.MarkAsPrivateActionOptionKey'),
      permission: permissions.can_edit_visibility_to_dwellers && isEditable,
      value: REQUEST_ACTION_MARK_AS_PRIVATE_MASS,
    },
    null,
    {
      disabled: isMixed,
      label: t('Request.ArchiveActionOptionKey'),
      permission: permissions.can_archive && !isArchive,
      tooltipContent: isMixed
        ? t('Request.MassArchiveMixedSelectionTooltipKey')
        : null,
      value: REQUEST_ACTION_ARCHIVE_MASS,
    },
    {
      disabled: isMixed,
      label: t('Request.RestoreActionOptionKey'),
      permission: permissions.can_restore && (isArchive || isMixed),
      tooltipContent: isMixed
        ? t('Request.MassRestoreMixedSelectionTooltipKey')
        : null,
      value: REQUEST_ACTION_RESTORE_MASS,
    },
  ]

  let list = items.filter(i => (i && !!i.permission) || !i)

  if (isNull(list[0]) || isNull(list[list.length - 1])) {
    list = list.filter(i => !isNull(i))
  }

  return list
}

export function getExportOptions({
  permissions,
  selectAllPages,
  selectedIds,
  t,
}) {
  const isSelectedEmpty = !selectAllPages && selectedIds.length === 0

  return [
    {
      disabled: isSelectedEmpty,
      label: t('RequestTable.exportTable'),
      tooltipContent: isSelectedEmpty
        ? t('RequestTable.ExportOptionDisabledTooltipKey')
        : null,
      value: RequestTableExportOptionValue.ExportTable,
    },
    ...(permissions.can_export_zip
      ? [
          {
            disabled: isSelectedEmpty,
            label: t('RequestTable.ExportEmailsFilesChatsOptionKey'),
            tooltipContent: isSelectedEmpty
              ? t('RequestTable.ExportOptionDisabledTooltipKey')
              : null,
            value: RequestTableExportOptionValue.ExportEmailsFilesChats,
          },
        ]
      : EMPTY_LIST),
    ...(permissions.can_export_invoices
      ? [
          {
            label: t('RequestTable.exportInvoice'),
            value: RequestTableExportOptionValue.ExportInvoice,
          },
        ]
      : EMPTY_LIST),
  ]
}

export function getRequestItemActionTypes(
  availableTypes = REQUEST_ITEM_ACTION_TYPES
) {
  return Object.values(getEntityActionTypeSelectOptions()).filter(item =>
    availableTypes.includes(item.value)
  )
}

export function getRequestMassActionTypes() {
  return Object.values(getEntityActionTypeSelectOptions())
}

export function getRequestEditStatusActionTypes() {
  return Object.values(getEntityActionTypeSelectOptions()).filter(
    isEditEntityActionType
  )
}
